import React, {useLayoutEffect, useState} from "react"
import Config from "../../library/Config"
import EditTabs from "./EditTabs"
import BackgroundToolbar from "./BackgroundToolbar"
import StickersToolbar from "./StickersToolbar"
import OverlaysToolbar from "./OverlaysToolbar"
import AiEffectToolbar from "./AiEffectToolbar"
import {CSSTransition, SwitchTransition} from "react-transition-group"
import * as StickerUtil from "../../utils/StickerUtil"

interface EditToolbarsProps {
  config: Config
  tools: string[]
  rawImage: string
  userImage: string
  onChangeBackground: (url: string) => void
  onChangeOverlay: (url: string | null) => void
  aiEffectRemainingRegenerations?: number
  aiEffectIsProcessing?: boolean
  onAiEffectRegenerate?: () => void
  aiImageHistory?: string[]
  selectedAiImageIndex?: number
  onAiImageSelect?: (index: number) => void
}

export default function EditToolbars({
  config,
  tools,
  rawImage,
  userImage,
  onChangeBackground,
  onChangeOverlay,
  aiEffectRemainingRegenerations,
  aiEffectIsProcessing,
  onAiEffectRegenerate,
  aiImageHistory,
  selectedAiImageIndex,
  onAiImageSelect,
}: EditToolbarsProps) {
  const [selectedToolIndex, setSelectedToolIndex] = useState(0)
  const [selectedBackgroundIndex, setSelectedBackgroundIndex] = useState(0)
  const [selectedOverlayIndex, setSelectedOverlayIndex] = useState(0)

  useLayoutEffect(() => {
    onChangeBackground(backgroundUrls[0])
    onChangeOverlay(config.overlays[0]?.url ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const theme = config.theme
  const bgUrls = config.backgrounds.map(b => b.url)
  const backgroundUrls = config.backgroundReplacementRequired ? bgUrls : [rawImage, ...bgUrls]

  const selectTool = (newIndex: number) => {
    if (newIndex < tools.length && newIndex !== selectedToolIndex) setSelectedToolIndex(newIndex)
  }

  const selectBackground = (newIndex: number) => {
    if (newIndex < backgroundUrls.length && newIndex !== selectedBackgroundIndex) {
      setSelectedBackgroundIndex(newIndex)
      onChangeBackground(backgroundUrls[newIndex])
    }
  }

  const selectOverlay = (newIndex: number) => {
    if (newIndex < config.overlays.length && newIndex !== selectedOverlayIndex) {
      setSelectedOverlayIndex(newIndex)
      onChangeOverlay(config.overlays[newIndex]?.url)
    }
  }

  const currentTool = tools[selectedToolIndex]

  return (
    <CSSTransition in appear timeout={300} classNames="fade">
      <div id="editToolsContainer">
        {tools.length > 1 &&
          <EditTabs
            theme={theme}
            tools={tools}
            selectedIndex={selectedToolIndex}
            onSelect={selectTool}
          />
        }

        <div style={theme.toolbarStyle}>
          <SwitchTransition>
            <CSSTransition key={currentTool} timeout={200} classNames="shortFade">
              <>
                {currentTool === "backgrounds" &&
                  <BackgroundToolbar
                    theme={theme}
                    backgroundUrls={backgroundUrls}
                    selectedIndex={selectedBackgroundIndex}
                    handleSelect={selectBackground}
                  />
                }

                {currentTool === "stickers" &&
                  <StickersToolbar
                    theme={theme}
                    stickers={config.stickers}
                    handleSticker={StickerUtil.addSticker}
                  />
                }

                {currentTool === "overlays" &&
                  <OverlaysToolbar
                    theme={theme}
                    background={userImage ?? ""}
                    overlays={config.overlays}
                    handleSelect={(i: number) => selectOverlay(i)}
                  />
                }

                {currentTool === "ai_effect" && onAiEffectRegenerate &&
                  <AiEffectToolbar
                    theme={theme}
                    remainingRegenerations={aiEffectRemainingRegenerations ?? 0}
                    isProcessing={aiEffectIsProcessing ?? false}
                    onRegenerate={onAiEffectRegenerate}
                    imageUrls={aiImageHistory ?? []}
                    selectedIndex={selectedAiImageIndex ?? 0}
                    onSelect={onAiImageSelect ?? (() => {})}
                  />
                }
              </>
            </CSSTransition>
          </SwitchTransition>
        </div>
      </div>
    </CSSTransition>
  )
}
